import { useTranslation } from 'react-i18next';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import type { Sortering } from '@xxtract/tabelfilters';
import { SorteerbareKop, ariaSort } from '@/components/tabel';
import { FACTUUR_LIJST } from '../factuurLijst';
import { formatInvoiceAmount, formatInvoiceDate, formatInvoicePeriod } from '../format';
import type { Invoice } from '../invoices.api';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';

interface InvoiceTableProps {
  invoices: Invoice[];
  selectedIds: ReadonlySet<string>;
  onToggleOne: (id: string) => void;
  onToggleAll: () => void;
  onDownloadPdf: (id: string) => void;
  downloadingId?: string | null;
  locale: string;
  /** With `onSorteer` the headers sort with a click (spec-tabelfilters CAP-5). */
  sortering?: Sortering;
  onSorteer?: (sleutel: string) => void;
}

export function InvoiceTable({
  invoices,
  selectedIds,
  onToggleOne,
  onToggleAll,
  onDownloadPdf,
  downloadingId = null,
  locale,
  sortering = [],
  onSorteer,
}: InvoiceTableProps) {
  const { t } = useTranslation();
  const allSelected = invoices.length > 0 && invoices.every((invoice) => selectedIds.has(invoice.id));

  return (
    <Table data-testid="invoice-table">
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleAll}
              aria-label={t('invoices.table.selectAll')}
            />
          </TableHead>
          {FACTUUR_LIJST.kolommen.map((kolom) => (
            <TableHead
              key={kolom.sleutel}
              aria-label={onSorteer ? t(kolom.kopSleutel) : undefined}
              aria-sort={onSorteer ? ariaSort(sortering, kolom.sleutel) : undefined}
              className={kolom.type === 'getal' ? 'text-right' : undefined}
            >
              {onSorteer ? <SorteerbareKop kolom={kolom} onKlik={() => onSorteer(kolom.sleutel)} sortering={sortering} /> : t(kolom.kopSleutel)}
            </TableHead>
          ))}
          <TableHead className="w-28" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((invoice) => {
          const selected = selectedIds.has(invoice.id);
          const emptyValue = t('common.notAvailable');
          const invoiceNumberDisplay = invoice.invoiceNumber ?? emptyValue;
          const invoiceDateDisplay = invoice.invoiceDate ? formatInvoiceDate(invoice.invoiceDate, locale) : emptyValue;
          const periodDisplay = invoice.period != null
            ? formatInvoicePeriod(invoice.period, invoice.invoiceDate, locale)
            : emptyValue;
          const descriptionDisplay = invoice.description ?? emptyValue;
          const amountDisplay = invoice.amountInclTax != null
            ? formatInvoiceAmount(invoice.amountInclTax, invoice.currency, locale)
            : emptyValue;
          const pdfButtonLabel = t('invoices.table.pdf');
          const selectOneLabel = t('invoices.table.selectOne', { invoiceNumber: invoice.invoiceNumber ?? invoice.id });

          return (
            <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`} aria-selected={selected}>
              <TableCell>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleOne(invoice.id)}
                  aria-label={selectOneLabel}
                />
              </TableCell>
              <TableCell className="font-medium text-[var(--foreground)]">{invoiceNumberDisplay}</TableCell>
              <TableCell>{invoiceDateDisplay}</TableCell>
              <TableCell>{periodDisplay}</TableCell>
              <TableCell className="text-[var(--foreground-muted)]">{descriptionDisplay}</TableCell>
              <TableCell className="text-right font-medium tabular-nums">{amountDisplay}</TableCell>
              <TableCell>
                <InvoiceStatusBadge status={invoice.status} overdue={invoice.overdue} dueDate={invoice.dueDate} locale={locale} />
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownloadPdf(invoice.id)}
                  disabled={downloadingId === invoice.id}
                >
                  <span className="material-symbols-outlined text-[16px]" aria-hidden="true">download</span>
                  {pdfButtonLabel}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
